import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Star,
  Speed,
  People,
  TrendingUp,
  CheckCircle,
  // Award,
  Handshake,
  Psychology,
  Verified,
  EmojiEvents,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import keyDifferentiatorsData from '../data/whyChooseUs/keyDifferentiatorsData.json';
import competitiveAdvantagesData from '../data/whyChooseUs/competitiveAdvantagesData.json';
import performamanceMetrixData from '../data/whyChooseUs/performamanceMetrixData.json';
import whatOurClientsSayData from '../data/whyChooseUs/whatOurClientsSayData.json';

const WhyChooseUs = () => {

  const keyDifferentiatorsDataIconMap = {
    Psychology: <Psychology sx={{ fontSize: 50 }} />,
    Star: <Star sx={{ fontSize: 50 }} />,
    Speed: <Speed sx={{ fontSize: 50 }} />,
    Handshake: <Handshake sx={{ fontSize: 50 }} />
  };

  const competitiveAdvantagesDataIconMap = {
    EmojiEvents: <EmojiEvents sx={{ fontSize: 40 }} />,
    Verified: <Verified sx={{ fontSize: 40 }} />,
    People: <People sx={{ fontSize: 40 }} />,
    TrendingUp: <TrendingUp sx={{ fontSize: 40 }} />
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '2.5rem', md: '3rem' },
              }}
            >
              Why Choose VinPriv?
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 300,
                fontSize: { xs: '1.2rem', md: '1.5rem' },
              }}
            >
              Your Success is Our Priority - Discover What Sets Us Apart
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Key Differentiators */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Our Key Differentiators
        </Typography>
        <Grid container spacing={4}>
          {keyDifferentiatorsData.map((differentiator, index) => (
            <Grid item xs={12} md={6} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: differentiator.color,
                          width: 70,
                          height: 70,
                          mr: 2,
                        }}
                      >
                        {keyDifferentiatorsDataIconMap[differentiator.icon]}
                      </Avatar>
                      <Box>
                        <Typography variant="h4" component="h3" fontWeight="bold">
                          {differentiator.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                          {differentiator.description}
                        </Typography>
                      </Box>
                    </Box>
                    <List>
                      {differentiator.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 35 }}>
                            <CheckCircle sx={{ color: differentiator.color, fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Competitive Advantages */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold' }}
          >
            Our Competitive Advantages
          </Typography>
          <Grid container spacing={4}>
            {competitiveAdvantagesData.map((advantage, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
                sx={{ display: 'flex' }} // Make each Grid item a flex container
              >
                <motion.div
                  style={{ flex: 1, display: 'flex' }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 60,
                        height: 60,
                        mb: 2,
                      }}
                    >
                      {competitiveAdvantagesDataIconMap[advantage.icon]}
                    </Avatar>

                    <Typography
                      variant="h3"
                      component="div"
                      color="primary"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {advantage.value}
                    </Typography>

                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      gutterBottom
                      sx={{ minHeight: 48 }} // Adjust if titles vary too much
                    >
                      {advantage.title}
                    </Typography>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {advantage.description}
                      </Typography>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Service Metrics */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          Performance Metrics
        </Typography>
        <Grid container spacing={4}>
          {performamanceMetrixData.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {metric.metric}
                  </Typography>
                  <Typography
                    variant="h3"
                    component="div"
                    sx={{ color: metric.color, mb: 2 }}
                    fontWeight="bold"
                  >
                    {metric.value}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={metric.value}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: metric.color,
                      },
                    }}
                  />
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Client Testimonials */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold' }}
          >
            What Our Clients Say
          </Typography>

          {/* Horizontal scroll container */}
          <Box
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'thin',
              backgroundColor: '#f5f5f5', // Or use #b0a8b9 for contrast
              borderRadius: 2,
              px: 2,
              py: 2,
              gap: 4,
              scrollbarColor: '#1976d2 #e0e0e0',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#1976d2',
                borderRadius: '4px',
              },
            }}
          >
            {whatOurClientsSayData.map((testimonial, index) => (
              <motion.div
                key={index}
                style={{
                  minWidth: '300px',
                  maxWidth: '360px',
                  flex: '0 0 auto',
                  display: 'flex',
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', mb: 2 }}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} sx={{ color: '#ffd700', fontSize: 20 }} />
                    ))}
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      flexGrow: 1,
                      mb: 3,
                      fontStyle: 'italic',
                      lineHeight: 1.6,
                    }}
                  >
                    "{testimonial.testimonial}"
                  </Typography>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {testimonial.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {testimonial.position}
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      {testimonial.company}
                    </Typography>
                  </Box>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>


      {/* Value Proposition */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{
            color: 'text.primary',
            fontWeight: 'bold',
            mb: 6,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100px',
              height: '4px',
              backgroundColor: 'primary.main',
              borderRadius: '2px',
            },
          }}
        >
          The VinPriv Advantage
        </Typography>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                Why We're Different
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                At VinPriv, we don't just provide consultants – we deliver solutions. Our approach
                combines deep technical expertise with industry knowledge to ensure your projects
                succeed from day one.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                We invest in long-term relationships, understanding that your success is our success.
                This partnership approach has resulted in a 95% client retention rate and countless
                successful project deliveries.
              </Typography>
              <Box sx={{ mt: 3 }}>
                {[
                  'Rigorous 5-stage screening process',
                  'Continuous skill development programs',
                  'Dedicated account management',
                  'Flexible engagement models',
                  '24/7 support and communication',
                ].map((point, index) => (
                  <Chip
                    key={index}
                    label={point}
                    sx={{ m: 0.5, bgcolor: 'primary.main', color: 'white' }}
                  />
                ))}
              </Box>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* <Paper
                elevation={4}
                sx={{
                  p: 4,
                  bgcolor: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  color: 'white',
                  textAlign: 'center',
                }}
              > */}
              <Paper
                elevation={4}
                sx={{
                  p: 4,
                  bgcolor: 'primary.main',
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Our Promise
                </Typography>
                <Typography variant="h6" sx={{ mb: 3, fontStyle: 'italic' }}>
                  "Delivering the Right Talent, at the Right Time, for the Right Results."
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="h3" fontWeight="bold">
                      48hrs
                    </Typography>
                    <Typography variant="body2">Average Deployment Time</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h3" fontWeight="bold">
                      100%
                    </Typography>
                    <Typography variant="body2">Satisfaction Guarantee</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Experience the VinPriv Difference
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 300 }}>
            Join our satisfied clients and discover why VinPriv is the preferred choice for IT talent solutions
          </Typography>
          <Typography variant="body1">
            Ready to get started? Contact us today for a consultation.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default WhyChooseUs;